import React from 'react';
import { Plus, Save } from 'lucide-react';
import { buttonBase, inputStyle, primaryButton } from './styles';
import {
  departmentLabelStyle,
  employeeCountStyle,
  fieldCaptionStyle,
  fieldStackStyle,
  manualActionsStyle,
  manualDateFieldStyle,
  manualEmployeeFieldStyle,
  manualFormStyle,
  manualPeriodFieldStyle,
  manualRowStyle,
  panelHeadStyle,
  panelStyle,
  panelSubStyle,
  panelTitleStyle,
  periodFieldStyle,
  ruleListStyle,
  rulePeriodStyle,
  ruleRowStyle,
} from './ContractRenewalSettingsParts.styles';

export const EMPTY_MANUAL = {
  user_id: '',
  initial_end_date: '',
  probation_period_value: '',
  probation_period_unit: 'day',
  renewal_period_value: '3',
  renewal_period_unit: 'month',
  memo: '',
};

export const PERIOD_UNITS = [
  ['day', '일'],
  ['month', '개월'],
  ['year', '년'],
];

export function onlyDigits(value) {
  return String(value || '').replace(/[^\d]/g, '');
}

export function DepartmentRuleSettings({ rules, drafts, loading, savingKey, onPatch, onSave }) {
  return (
    <div style={panelStyle}>
      <div style={panelHeadStyle}>
        <div>
          <div style={panelTitleStyle}>부서별 자동등록 설정</div>
          <div style={panelSubStyle}>
            자동등록은 새 대상 생성에만 쓰고, 이미 생성된 계약종료일은 자동으로 덮어쓰지 않습니다.
          </div>
        </div>
        {loading && <span style={panelSubStyle}>불러오는 중...</span>}
      </div>
      <div style={ruleListStyle}>
        {rules.map((rule) => {
          const draft = drafts[rule.department_name] || {};
          const saving = savingKey === rule.department_name;
          return (
            <div key={rule.department_name} style={ruleRowStyle}>
              <label style={departmentLabelStyle}>
                <input
                  type="checkbox"
                  checked={Boolean(draft.auto_register)}
                  onChange={(event) => onPatch(rule.department_name, { auto_register: event.target.checked })}
                />
                <span style={{ minWidth: 0 }}>{rule.department_name}</span>
                <small style={employeeCountStyle}>{rule.current_employee_count || 0}명</small>
              </label>
              <RulePeriodField
                label="초기"
                value={draft.initial_contract_value || 85}
                unit={draft.initial_contract_unit || 'day'}
                onValue={(value) => onPatch(rule.department_name, { initial_contract_value: value })}
                onUnit={(value) => onPatch(rule.department_name, { initial_contract_unit: value })}
              />
              <RulePeriodField
                label="갱신"
                value={draft.renewal_period_value || 3}
                unit={draft.renewal_period_unit || 'month'}
                onValue={(value) => onPatch(rule.department_name, { renewal_period_value: value })}
                onUnit={(value) => onPatch(rule.department_name, { renewal_period_unit: value })}
              />
              <RulePeriodField
                label="수습"
                value={draft.probation_period_value || ''}
                unit={draft.probation_period_unit || 'day'}
                placeholder="없음"
                onValue={(value) => onPatch(rule.department_name, { probation_period_value: value })}
                onUnit={(value) => onPatch(rule.department_name, { probation_period_unit: value })}
              />
              <button type="button" style={primaryButton(saving)} disabled={saving} onClick={() => onSave(rule)}>
                <Save size={14} /> 저장
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ManualEnrollForm({ candidates, manual, saving, onManualChange, onSave }) {
  return (
    <div style={panelStyle}>
      <div style={panelTitleStyle}>직접 등록</div>
      <div style={panelSubStyle}>
        자동등록 대상이 아닌 직원을 계약갱신관리 대상에 추가합니다.
      </div>
      <div style={manualFormStyle}>
        <div style={manualRowStyle}>
          <label style={{ ...fieldStackStyle, ...manualEmployeeFieldStyle }}>
            <span>직원</span>
            <select
              style={{ ...inputStyle, width: '100%' }}
              value={manual.user_id}
              onChange={(event) => {
                const next = candidates.find((item) => String(item.id) === String(event.target.value));
                onManualChange({
                  user_id: event.target.value,
                  initial_end_date: next?.existing_end_date || '',
                });
              }}
            >
              <option value="">직원 선택</option>
              {candidates.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} · {item.department || '-'}{item.existing_end_date ? ` · 기존 ${item.existing_end_date}` : ''}
                </option>
              ))}
            </select>
          </label>
          <label style={{ ...fieldStackStyle, ...manualDateFieldStyle }}>
            <span>계약 만료일</span>
            <input
              type="date"
              style={{ ...inputStyle, width: '100%' }}
              value={manual.initial_end_date || ''}
              onChange={(event) => onManualChange({ initial_end_date: event.target.value })}
            />
          </label>
          <PeriodField
            label="수습기간"
            value={manual.probation_period_value}
            unit={manual.probation_period_unit}
            placeholder="없음"
            style={manualPeriodFieldStyle}
            onValue={(value) => onManualChange({ probation_period_value: value })}
            onUnit={(value) => onManualChange({ probation_period_unit: value })}
          />
          <PeriodField
            label="갱신기간"
            value={manual.renewal_period_value || '3'}
            unit={manual.renewal_period_unit}
            style={manualPeriodFieldStyle}
            onValue={(value) => onManualChange({ renewal_period_value: value })}
            onUnit={(value) => onManualChange({ renewal_period_unit: value })}
          />
          <div style={manualActionsStyle}>
            <button type="button" style={buttonBase} disabled={saving} onClick={onSave}>
              <Plus size={14} /> 등록
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RulePeriodField({ label, value, unit, placeholder, onValue, onUnit }) {
  return (
    <label style={rulePeriodStyle}>
      <span style={fieldCaptionStyle}>{label}</span>
      <input
        type="text"
        inputMode="numeric"
        value={value || ''}
        onChange={(event) => onValue(onlyDigits(event.target.value))}
        placeholder={placeholder}
        style={{ ...inputStyle, width: 64 }}
      />
      <select
        style={{ ...inputStyle, width: 78, padding: '0 8px' }}
        value={unit || 'day'}
        onChange={(event) => onUnit(event.target.value)}
      >
        {PERIOD_UNITS.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>{optionLabel}</option>
        ))}
      </select>
    </label>
  );
}

function PeriodField({ label, value, unit, placeholder, style, onValue, onUnit }) {
  return (
    <label style={{ ...fieldStackStyle, ...(style || {}) }}>
      <span>{label}</span>
      <div style={periodFieldStyle}>
        <input
          type="text"
          inputMode="numeric"
          style={{ ...inputStyle, width: '100%' }}
          value={value || ''}
          onChange={(event) => onValue(onlyDigits(event.target.value))}
          placeholder={placeholder}
        />
        <select
          style={{ ...inputStyle, width: 86, padding: '0 8px' }}
          value={unit || 'day'}
          onChange={(event) => onUnit(event.target.value)}
        >
          {PERIOD_UNITS.map(([optionValue, optionLabel]) => (
            <option key={optionValue} value={optionValue}>{optionLabel}</option>
          ))}
        </select>
      </div>
    </label>
  );
}
